import React, { Fragment, useCallback } from 'react';
import { Dimensions, StyleSheet, Text, View, Linking, Platform } from 'react-native';
import { ComponentWrapper } from '../../../../helperComponents/componentWrapper/ComponentWrapper';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { trpc } from '../../../../../apiCalls/trpcClient';
import { UserVisit } from '@sapphicsavvy/business';
import { ScreenProps } from '../../../../../typesAndInterfaces/componentProps';
import OverviewScreen from '../../../../helperComponents/OverviewScreen';
import { FONT_STYLES, textStyles } from '../../../../../constants/textStyles';
import PeopleIcon from '../../../../../resources/svgComponents/PeopleIcon';
import { mbShadow, mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { useTracker } from '../../../../../hooks/useTracker';

export function Socialize({ route }: ScreenProps<'Socialize'>) {
    const { track } = useTracker();
    const { data: forumData, isLoading } = trpc.user.getForumSSOUrl.useQuery({});

    const handleOpenForum = useCallback(async () => {
        if (!forumData?.url) return;

        track(UserVisit.join_chat);

        try {
            const supported = await Linking.canOpenURL(forumData.url);
            if (supported) {
                await Linking.openURL(forumData.url);
            } else {
                console.error('Cannot open forum URL:', forumData.url);
            }
        } catch (error) {
            console.error('Error opening forum:', error);
        }
    }, [forumData, track]);

    return (
        <OverviewScreen
            showBackBtn
            topSectionStyle={styles.topSection}
            topSectionChildren={
                <Fragment>
                    <PeopleIcon size={49} />
                    <Text style={styles.titleText}>SOCIALIZE</Text>
                    <Text style={styles.subTitleText}>Join our community forum</Text>
                </Fragment>
            }
            bottomSectionStyle={styles.bottomSection}
        >
            <View style={styles.shape}>
                <ComponentWrapper>
                    <View style={styles.contentContainer}>
                        <TouchableOpacity
                            onPress={handleOpenForum}
                            style={styles.roomButton}
                            disabled={isLoading || !forumData?.url}
                        >
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={styles.roomTypeText}>
                                    COMMUNITY FORUM
                                </Text>
                            </View>
                            <View style={styles.joinWrapper}>
                                <Text style={styles.joinText}>
                                    {isLoading ? 'Loading...' : 'Join'}
                                </Text>
                            </View>
                        </TouchableOpacity>
                    </View>
                </ComponentWrapper>
            </View>
        </OverviewScreen>
    );
}

const { width } = Dimensions.get('window');

const styles = StyleSheet.create({
    topSection: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 20,
        height: '35%',
    },
    bottomSection: {
        height: '65%',
        paddingTop: 0,
        backgroundColor: '#EEEEF2',
    },
    shape: {
        flex: 1,
        borderTopLeftRadius: 36,
        borderTopRightRadius: 36,
        overflow: 'hidden',
        backgroundColor: '#EEEEF2',
    },
    titleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    titleText: mbTextStyles([
        textStyles.largestText,
        {
            fontSize: 45,
            fontFamily: FONT_STYLES.Tabasco_400,
            color: '#FFFFFF',
            fontWeight: '400',
            marginBottom: 10,
            marginTop: 5,
        },
    ]),
    joinText: mbTextStyles([
        textStyles.largestText,
        {
            fontSize: 18,
            fontFamily: FONT_STYLES.Inter_600,
            color: '#FFFFFF',
        },
    ]),
    joinWrapper: {
        paddingVertical: 10,
        paddingHorizontal: 15,
        backgroundColor: '#F35E2E',
        borderRadius: 29,
    },
    subTitleText: mbTextStyles([
        textStyles.largeText,
        {
            color: '#FFFFFF',
            fontSize: 18,
            fontWeight: '700',
            marginBottom: 20,
        },
    ]),
    contentContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    roomButton: {
        marginVertical: 10,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 20,
        backgroundColor: '#F0F0F3',
        borderRadius: 999,
        ...mbShadow({
            offsetHeight: 2,
            offsetWidth: 2,
            color: '#565656',
            opacity: 0.1,
            elevation: 10,
            radius: 20,
        }),
        width: width * 0.9,
    },
    roomTypeText: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#F35E2E',
        fontFamily: FONT_STYLES.Tabasco_400,
        marginRight: 6,
    },
    roomParticipantsText: {
        fontSize: 20,
        color: '#1C1D20F4',
        fontFamily: FONT_STYLES.Tabasco_400,
        marginTop: 2,
    },
});
